import { HttpService } from '../helper/httpService';
import { Constants } from '../helper/constants';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ReportFormatPipe } from '../pipes/report-format.pipe';
import { CustomReportModel } from '../viewmodels/customReport.model';

import { json2csv } from 'json-2-csv';
import { saveAs } from 'file-saver';
import { Global } from '../../../app.global';
import { SettingsService } from '../../../authentication/services/settings.service';
import { OffenceTypeViewModel } from '../viewmodels/offence-type-viewmodel';

@Injectable()
export class ReportsService {
  private reportServiceUrl = Constants.serverUrl + 'api/reports/';
  // private multiCouncil = false;

  constructor(
    public http: HttpService,
    private settingsService: SettingsService,
    private global: Global
  ) {}

  /** getDepartmentPerformance */
  getDepartmentPerformance(
    periodParam: string,
    startDateParam: Date,
    endDateParam: Date
  ): Observable<any> {
    return this.http.Get(
      this.reportServiceUrl +
        'getDepartmentPerformance' +
        '/' +
        periodParam +
        '/' +
        startDateParam.toDateString() +
        '/' +
        endDateParam.toDateString()
    );
  }

  /** getTeamLeadPerformance */
  getTeamLeadPerformance(
    teamLeadId: number,
    periodParam: string,
    startDateParam: Date,
    endDateParam: Date
  ): Observable<any> {
    return this.http.Get(
      this.reportServiceUrl +
        'getTeamLeadPerformance' +
        '/' +
        teamLeadId +
        '/' +
        periodParam +
        '/' +
        startDateParam.toDateString() +
        '/' +
        endDateParam.toDateString()
    );
  }

  /** getBacklogPerformance */
  getBacklogPerformance(
    periodParam: string,
    startDateParam: Date,
    endDateParam: Date
  ): Observable<any> {
    return this.http.Get(
      this.reportServiceUrl +
        'getBacklogPerformance' +
        '/' +
        periodParam +
        '/' +
        startDateParam.toDateString() +
        '/' +
        endDateParam.toDateString()
    );
  }

  // getAllTeamLeaders
  getAllTeamLeaders(): Observable<any> {
    return this.http.Get(this.reportServiceUrl + 'findAllTeamLeaders');
  }

  // getPcnPrefixes
  getPcnPrefixes(): Observable<any> {
    return this.http.Get(this.reportServiceUrl + 'getPcnPrefixes');
  }

  // getOffences
  getOffences(): Observable<any> {
    return this.http.Get(this.reportServiceUrl + 'getOffences');
  }

  // get Stage Groups
  getStageGroups(): Observable<any> {
    return this.http.Get(this.reportServiceUrl + 'getStageGroups');
  }

  /** findUsersByTeamLeadersIds */
  findUsersByTeamLeadersIds(teamLeadersIds: Array<number>): Observable<any> {
    const data = JSON.stringify(teamLeadersIds);
    return this.http.Post(this.reportServiceUrl + 'findUsersByTeamLeadersIds/', data);
  }

  /** getCustomReports */
  getCustomReports(customReportModel): Observable<any> {
    const data = JSON.stringify(customReportModel);
    return this.http.Post(this.reportServiceUrl + 'getCustomReports/', data);
  }

  /** getLetterByRepId */
  getLetterByRepId(repId: number): Observable<any> {
    return this.http.Get(this.reportServiceUrl + 'getLetterByRepId' + '/' + repId);
  }

  getFormattedTeamsDepartmentReport(teamsReport) {
    return teamsReport.map((team) => {
      team.queriesAnswered = ReportFormatPipe.prototype.transform(team.queriesAnswered, 'dash');
      team.percentageChange = ReportFormatPipe.prototype.transform(
        team.percentageChange,
        'percentDash'
      );
      team.percentageOfTotal = ReportFormatPipe.prototype.transform(
        team.percentageOfTotal,
        'percentDash'
      );
      team.percentageCancelled = ReportFormatPipe.prototype.transform(
        team.percentageCancelled,
        'percentDash'
      );
      team.percentageBespoke = ReportFormatPipe.prototype.transform(
        team.percentageBespoke,
        'percentDash'
      );
      team.percentageToSupervisor = ReportFormatPipe.prototype.transform(
        team.percentageToSupervisor,
        'percentDash'
      );
      team.percentageUnwantedFollowUps = ReportFormatPipe.prototype.transform(
        team.percentageUnwantedFollowUps,
        'percentDash'
      );
      team.percentageChangeUnwantedFollowUps = ReportFormatPipe.prototype.transform(
        team.percentageChangeUnwantedFollowUps,
        'percentDash'
      );
      team.outputPerDay = ReportFormatPipe.prototype.transform(team.outputPerDay, 'dash');
      team.percentageChangeOutput = ReportFormatPipe.prototype.transform(
        team.percentageChangeOutput,
        'percentDash'
      );
      team.percentageTimeOnLetters = ReportFormatPipe.prototype.transform(
        team.percentageTimeOnLetters,
        'percentDash'
      );
      return team;
    });
  }

  getFormattedTotalTeamsDepartmentReport(totalTeamsReport) {
    totalTeamsReport.name = 'dept. summary';
    totalTeamsReport.queriesAnswered = ReportFormatPipe.prototype.transform(
      totalTeamsReport.queriesAnswered,
      'value'
    );
    totalTeamsReport.percentageChange = ReportFormatPipe.prototype.transform(
      totalTeamsReport.percentageChange,
      'percentDash'
    );
    totalTeamsReport.percentageOfTotal = ReportFormatPipe.prototype.transform(
      totalTeamsReport.percentageOfTotal,
      'percentDashFull'
    );
    totalTeamsReport.percentageCancelled = ReportFormatPipe.prototype.transform(
      totalTeamsReport.percentageCancelled,
      'percentDash'
    );
    totalTeamsReport.percentageBespoke = ReportFormatPipe.prototype.transform(
      totalTeamsReport.percentageBespoke,
      'percentDash'
    );
    totalTeamsReport.percentageToSupervisor = ReportFormatPipe.prototype.transform(
      totalTeamsReport.percentageToSupervisor,
      'percentDash'
    );
    totalTeamsReport.percentageUnwantedFollowUps = ReportFormatPipe.prototype.transform(
      totalTeamsReport.percentageUnwantedFollowUps,
      'percentDash'
    );
    totalTeamsReport.percentageChangeUnwantedFollowUps = ReportFormatPipe.prototype.transform(
      totalTeamsReport.percentageChangeUnwantedFollowUps,
      'percentDash'
    );
    totalTeamsReport.outputPerDay = ReportFormatPipe.prototype.transform(
      totalTeamsReport.outputPerDay,
      'value'
    );
    totalTeamsReport.percentageChangeOutput = ReportFormatPipe.prototype.transform(
      totalTeamsReport.percentageChangeOutput,
      'percentDash'
    );
    totalTeamsReport.percentageTimeOnLetters = ReportFormatPipe.prototype.transform(
      totalTeamsReport.percentageTimeOnLetters,
      'percentDash'
    );
    return totalTeamsReport;
  }

  exportDepartmentObject(team) {
    return {
      team: team.name,
      answered: team.queriesAnswered,
      change: team.percentageChange,
      'of total': team.percentageOfTotal,
      "canc'd": team.percentageCancelled,
      bespoke: team.percentageBespoke,
      override: team.percentageToSupervisor,
      received: team.previousUnwantedFollowUps,
      'quality change': team.percentageChangeUnwantedFollowUps,
      'pp per day': team.outputPerDay,
      'output rate change': team.percentageChangeOutput,
      'on letters': team.percentageTimeOnLetters,
    };
  }

  exportCustomObject(report) {
    return {
      user: report.fullname,
      date: report.date,
      PCN: report.pcn,
      stage: report.stage,
      decision: report.decision,
      charge: report.fineNow,
      progression: report.progression,
      bespoke: report.bespoke,
      override: report.override,
      UFU: report.ufu,
    };
  }

  getFormattedBacklogReport(backlog) {
    backlog.informalChallengeAverageWorkingDays = ReportFormatPipe.prototype.transform(
      backlog.informalChallengeAverageWorkingDays,
      'value'
    );
    backlog.informalChallengeAverageDays = ReportFormatPipe.prototype.transform(
      backlog.informalChallengeAverageDays,
      'value'
    );
    backlog.informalChallengeChange = ReportFormatPipe.prototype.transform(
      backlog.informalChallengeChange,
      'percentDash'
    );
    backlog.representationAverageWorkingDays = ReportFormatPipe.prototype.transform(
      backlog.representationAverageWorkingDays,
      'value'
    );
    backlog.representationAverageDays = ReportFormatPipe.prototype.transform(
      backlog.representationAverageDays,
      'value'
    );
    backlog.representationChange = ReportFormatPipe.prototype.transform(
      backlog.representationChange,
      'percentDash'
    );
    backlog.otherAverageWorkingDays = ReportFormatPipe.prototype.transform(
      backlog.otherAverageWorkingDays,
      'value'
    );
    backlog.otherAverageDays = ReportFormatPipe.prototype.transform(
      backlog.otherAverageDays,
      'value'
    );
    backlog.otherChange = ReportFormatPipe.prototype.transform(backlog.otherChange, 'percentDash');
    backlog.backlogValue = ReportFormatPipe.prototype.transform(backlog.backlogValue, 'currency');
    return backlog;
  }

  exportBacklogObject(backlog) {
    return {
      'wkg days': backlog.informalChallengeAverageWorkingDays,
      'cal days': backlog.informalChallengeAverageDays,
      change: backlog.informalChallengeChange,
      'rep wkg days': backlog.representationAverageWorkingDays,
      'rep cal days': backlog.representationAverageDays,
      'rep change': backlog.representationChange,
      'other wkg days': backlog.otherAverageWorkingDays,
      'other cal days': backlog.otherAverageDays,
      'other change': backlog.otherChange,
      ' ': backlog.backlogValue,
    };
  }

  exportExcelDocument(reportJson, reportFields, dateRange, extraField) {
    // const converter = require('json-2-csv');
    // const Json2csvParser = json2csv.Parser;
    const documentName = 'reports.csv';
    // const json2csvParser = new Json2csvParser({ reportFields });

    let csv = '"' + dateRange + '"' + '\r\n';
    if (extraField !== '') {
      csv += extraField;
    }
    // csv += json2csvParser.parse(reportJson);
    // csv += converter.json2csv(reportJson);
    csv += json2csv(reportJson);
    const contentType = 'text/csv';
    const csvFile = new Blob(['\ufeff', csv], { type: contentType });
    saveAs(csvFile, documentName);

    // const workBook = xlsx.utils.book_new(); // create a new blank book
    // const workSheet = xlsx.utils.json_to_sheet(testJson);

    // xlsx.utils.book_append_sheet(workBook, workSheet, 'data'); // add the worksheet to the book
    // xlsx.writeFile(workBook, 'abc.xlsx'); // initiate a file download in browser
  }

  // getTeamStats
  getTeamStats(): Observable<any> {
    return this.http.Get(this.reportServiceUrl + 'getTeamStats');
  }

  // getLetterDashboard
  getLetterDashboard(): Observable<any> {
    return this.http.Get(this.reportServiceUrl + 'getLetterDashboard');
  }
}
